import { ReactComponent as IconChevronLeft } from "@/assets/svgs/chevron-left-medium.svg";
import { ReactComponent as IconChevronRight } from "@/assets/svgs/chevron-right-medium.svg";
import { ReactComponent as IconSort } from "@/assets/svgs/sort-icon.svg";
import Checkbox from '@/componentsDS/Checkbox/Checkbox';
import Search from "@/componentsDS/Search";
import { produce } from 'immer';
import { uniqueId } from "lodash";
import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from "react";

const NoMessage = ({ text = "No data available" }) => {
	return (
		<div className="selectable-data__table__empty__icon">
			<span>{text}</span>
		</div>
	);
};

NoMessage.propTypes = {
	text: PropTypes.string,
}

const PrincipalsTable = ({ 
	data = [],
	setData,
	setSelectedItem,
	
	titleHeader = 'Items',
	AvatarIcon = null,
	nameKey = 'name',
	subTextKey = null,
	subTextLabel = '',
	
	showSearch = false,
	searchPlaceholder = 'Search...',
	searchFields = ['name'],
	
	selectedKey = 'id',
	defaultSortBy = 'name',
	itemsPerPage = 30,
	
	emptyText = "No data available",
	emptySearchText = "No data found",
}) => {

	/*=================================================
		Search State
	/================================================*/
	const [searchValue, setSearchValue] = useState('')
	
	const handleSearchChange = (e) => setSearchValue(e.target.value)
	
	const handleClearSearch = () => setSearchValue('')

	/*=================================================
		Pagination
	/================================================*/
	const [currentPage, setCurrentPage] = useState(1)
	const [totalPage, setTotalPage] = useState(1)
	const [totalItemCount, setTotalItemCount] = useState(0)
	const [filteredData, setFilteredData] = useState([])
	const [displayData, setDisplayData] = useState([])

	const getPageStart = (pageSize, pageNo) => pageSize * pageNo

	const getPageLabel = (total, pageSize, pageNo) => {
		if (filteredData?.length === 0) return "0"

		const start = Math.max(getPageStart(pageSize, pageNo), 0)
		const end = Math.min(getPageStart(pageSize, pageNo + 1), total)

		return `${start + 1} - ${end}`
	}

	const handleCurrentInput = (e) => {
		const value = e.target?.value === '' ? 1 : e.target.value
		const current = Number.parseInt(value, 10)

		if (current < 1) return setCurrentPage(1)
		if (current > totalPage) return setCurrentPage(totalPage)

		setCurrentPage(current)
	}

	const goToPrev = () => {
		if (currentPage === 1) return
		setCurrentPage((prev) => prev - 1)
	}

	const goToNext = () => {
		if (currentPage === totalPage) return
		setCurrentPage((prev) => prev + 1)
	}

	useEffect(() => {
		if (currentPage > totalPage && totalPage > 0) {
			setCurrentPage(1)
		}
	}, [filteredData, currentPage, totalPage])

	/*=================================================
		Sort
	/================================================*/
	const [sortConfig, setSortConfig] = useState({ 
		sortBy: defaultSortBy, 
		direction: true // true = ascending, false = descending
	});

	const handleSort = (sortBy) => {
		setSortConfig(prev => ({
			sortBy,
			direction: prev.sortBy === sortBy ? !prev.direction : true
		}));
	}

	const getSortClassName = (name) => {
		if (!sortConfig || sortConfig.sortBy !== name) return "";
		return sortConfig.direction ? "is-asc" : "is-desc";
	}

	/*=================================================
		Data Processing
	/================================================*/

	const getValueByPath = (obj, path) => {
		if (!path) return undefined;
		const keys = path.split('.');
		let current = obj;
		for (const key of keys) {
			current = current?.[key];
			if (current === undefined || current === null) return current;
		}
		return current;
	};
	
	const applySearchFilter = useCallback((dataList, searchVal) => {
		const needle = searchVal?.trim()?.toLowerCase();
		if (!needle?.length) return dataList;

		return dataList.filter((item) =>
			searchFields.some((field) => {
				const fieldValue = getValueByPath(item, field);
				return fieldValue?.toString().toLowerCase().includes(needle);
			})
		);
	}, [searchFields]);

	const applySorting = useCallback((dataList) => {
		if (!sortConfig.sortBy?.length) return dataList;

		const sortedList = [...dataList];
		sortedList.sort((a, b) => {
			const aVal = a?.[sortConfig?.sortBy];
			const bVal = b?.[sortConfig?.sortBy];
			return (aVal > bVal ? 1 : -1) * (sortConfig?.direction ? 1 : -1);
		});

		return sortedList;
	}, [sortConfig]);

	const applyPagination = useCallback((dataList, page) => {
		const start = page > 1 ? (page - 1) * itemsPerPage : 0;
		return dataList.slice(start, start + itemsPerPage);
	}, [itemsPerPage]);

	useEffect(() => {
		if (!data?.length) {
			setFilteredData([]);
			setDisplayData([]);
			setTotalPage(1);
			setTotalItemCount(0);
			return;
		}

		let processedData = [...data];
		
		processedData = applySearchFilter(processedData, searchValue);
		processedData = applySorting(processedData);

		setFilteredData(processedData);
		setTotalPage(Math.ceil(processedData.length / itemsPerPage));
		setTotalItemCount(processedData.length);
		
		const paginatedData = applyPagination(processedData, currentPage);
		setDisplayData(paginatedData);
	}, [currentPage, searchValue, data, applySearchFilter, applySorting, applyPagination, itemsPerPage]);

	/*=================================================
		Selection Logic
	/================================================*/
	const isAllChecked = (itemData) => {
		return itemData?.length && 
			itemData?.every((item) => item?.$selected === true || item?.$disabled === true) && 
			itemData?.some((item) => item?.$selected === true);
	};

	const handleUpdateItemSelect = (item) => {
		if (item?.$disabled) return;

		const index = data?.findIndex(dataItem => dataItem?.[selectedKey] === item?.[selectedKey]);
		if (index === -1) return;

		const updatedItems = produce(data, draft => {
			draft[index].$selected = !draft[index].$selected;
		});

		setData(updatedItems);
		
		if (setSelectedItem) {
			const selected = updatedItems.filter(item => item.$selected);
			setSelectedItem(selected);
		}
	}

	const handleUpdateAllSelect = (checked) => {
		if (!data?.length) return;
		const displayedKeys = new Set(displayData?.map((displayItem) => displayItem?.[selectedKey]));

		const updatedItems = produce(data, draft => {
			for (const item of draft) {
				if (displayedKeys.has(item?.[selectedKey])) {
					item.$selected = checked;
				}
			}
		});

		setData(updatedItems);
		
		if (setSelectedItem) {
			const selected = updatedItems.filter(item => item.$selected);
			setSelectedItem(selected);
		}
	}

	/*=================================================
		Render
	/================================================*/
	const selectedCount = data?.filter(s => s?.$selected)?.length || 0;
	
	return (
		<div
			data-testid="selectable-data__table"
			className="selectable-data__table__wrapper">
			
			{showSearch && (
				<div className="selectable-data__table__search__box">
					<Search
						placeholder={searchPlaceholder}
						value={searchValue || ""}
						onChange={handleSearchChange}
						onCancel={handleClearSearch}
					/>
					<div className="selectable-data__table__search__count">
						{selectedCount > 0 && (
							<span>{selectedCount} Selected</span>
						)}
					</div>
				</div>
			)}
			
			<div className="selectable-data__table">
				<div className="selectable-data__table__container">
					<div className="selectable-data__table__header__row">
						<div
							className={`selectable-data__table__header__text title__grey selectable-data__table__table__head__title__sort-order link ${getSortClassName(defaultSortBy)}`}
							role="button"
							tabIndex={0}
							onClick={() => handleSort(defaultSortBy)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleSort(defaultSortBy);
								}
							}}
						>
							{displayData?.length > 0 && (<Checkbox
								id="select-all-checkbox"
								checked={isAllChecked(displayData)}
								disabled={displayData?.every(s => s?.$disabled) ?? false}
								indeterminate={!isAllChecked(displayData) && displayData?.some(item => item?.$selected)}
								onChange={(checked) => handleUpdateAllSelect(checked)}
							/>)}
							<div className="selectable-data__table__header__title">
								{titleHeader} <IconSort className="selectable-data__table__table__head__icon__sort-order" />
							</div>
						</div>
					</div>

					{displayData?.length > 0 ? (
						<>
							{displayData.map((item) => (
								<div
									className="selectable-data__table__body__row"
									key={item[selectedKey] || uniqueId()}
									role="button"
									onClick={() => handleUpdateItemSelect(item)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleUpdateItemSelect(item);
										}
									}}
									tabIndex={0}
								>
									<Checkbox
										id={`item-checkbox-${item[selectedKey] || uniqueId()}`}
										checked={item?.$selected}
										disabled={item?.$disabled}
										onChange={() => handleUpdateItemSelect(item)}
									/>
									<div className="selectable-data__table__data__row">
										{AvatarIcon && (
											<div className="selectable-data__table__avatar">
												<AvatarIcon />
											</div>
										)}
										<div className="selectable-data__table__data__col__info">
											<div data-testid="item-name" className="selectable-data__table__data__name">
												{item?.[nameKey]}
											</div>
											{subTextKey && item?.[subTextKey] && (
												<div className="selectable-data__table__data__subtext">
													{subTextLabel && `${subTextLabel}: `}{item?.[subTextKey]}
												</div>
											)}
										</div>
									</div>
								</div>
							))}
						</>
					) : (
						<div className="selectable-data__table__empty">
							<NoMessage text={data?.length && searchValue?.length ? emptySearchText : emptyText} />
						</div>
					)}

					{displayData?.length > 0 && (
						<div className="selectable-data__table__table__footer">
							<div className="selectable-data__table__table__footer__row">
								<div className="selectable-data__table__table__footer__row__left">
									<div className="selectable-data__table__table__pagination">
										<input
											data-testid="pagination-input"
											className="selectable-data__table__table__pagination__page"
											type="number"
											value={currentPage}
											onChange={handleCurrentInput}
											min="1"
										/>
										<span className="selectable-data__table__table__pagination__page__meta">
											{' '}of {totalPage || 1} pages{' '}
										</span>
										<button
											data-testid="pagination-prev"
											className="selectable-data__table__table__pagination__prev"
											onClick={goToPrev}
											disabled={currentPage === 1}
										>
											<IconChevronLeft />
										</button>
										<button
											data-testid="pagination-next"
											className="selectable-data__table__table__pagination__next"
											onClick={goToNext}
											disabled={!totalPage || currentPage === totalPage}
										>
											<IconChevronRight />
										</button>
									</div>
								</div>
								<div className="selectable-data__table__table__footer__row__right">
									<div className="selectable-data__table__table__meta">
										Showing{' '}
										{getPageLabel(totalItemCount, itemsPerPage, currentPage - 1)}{' '}
										of{' '}
										{filteredData.length === 0 && searchValue.length > 0
											? data.length
											: totalItemCount || 0}{' '}
										entries
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
};

PrincipalsTable.propTypes = {
	data: PropTypes.array.isRequired,
	setData: PropTypes.func.isRequired,
	setSelectedItem: PropTypes.func,
	
	titleHeader: PropTypes.string,
	AvatarIcon: PropTypes.elementType,
	nameKey: PropTypes.string,
	subTextKey: PropTypes.string,
	subTextLabel: PropTypes.string,
	
	showSearch: PropTypes.bool,
	searchPlaceholder: PropTypes.string,
	searchFields: PropTypes.arrayOf(PropTypes.string),
	
	selectedKey: PropTypes.string,
	defaultSortBy: PropTypes.string,
	itemsPerPage: PropTypes.number,
	
	emptyText: PropTypes.string,
	emptySearchText: PropTypes.string,
};

export default PrincipalsTable;
